import { zoomAt } from "../render/Viewport";
import type { Tool, PointerSample, ToolContext } from "./Tool";

// Click to zoom in 2x at click point. Alt-click to zoom out 0.5x.
// Horizontal drag right = scrubby zoom in; left = scrubby zoom out (PS default since CS5).
// Click-drag (vertical or small) falls back to marquee-zoom.

interface ZoomMarquee {
  startX: number;
  startY: number;
  endX: number;
  endY: number;
}

interface ScrubbyDrag {
  /** Screen X where the drag originated — used as zoom anchor. */
  originX: number;
  /** Screen Y where the drag originated. */
  originY: number;
  /** Last processed X so we can compute per-frame delta. */
  lastX: number;
}

/** Injected by main.ts so ZoomTool can read viewport dimensions without touching DOM. */
export type ViewportSizeProvider = () => { width: number; height: number };

export class ZoomTool implements Tool {
  readonly id = "zoom";
  readonly shortcut = "Z";
  private altHeld = false;
  private marquee: ZoomMarquee | null = null;
  private scrubby: ScrubbyDrag | null = null;

  /** Set by main.ts to return the canvas CSS dimensions. */
  viewportSizeProvider: ViewportSizeProvider | null = null;

  setAlt(held: boolean): void {
    this.altHeld = held;
  }

  onPointerDown(ctx: ToolContext, s: PointerSample): void {
    this.scrubby = { originX: s.x, originY: s.y, lastX: s.x };
    this.marquee = { startX: s.x, startY: s.y, endX: s.x, endY: s.y };
  }

  onPointerMove(ctx: ToolContext, s: PointerSample): void {
    if (!this.scrubby) return;
    const totalDx = s.x - this.scrubby.originX;
    const totalDy = s.y - this.scrubby.originY;
    // Classify as scrubby once horizontal motion clearly dominates.
    if (Math.abs(totalDx) > Math.abs(totalDy) && Math.abs(totalDx) > 5) {
      // Scrubby zoom: each pixel of horizontal movement = ~1% zoom step.
      const deltaSinceLastFrame = s.x - this.scrubby.lastX;
      if (deltaSinceLastFrame !== 0) {
        const factor = deltaSinceLastFrame > 0
          ? 1 + deltaSinceLastFrame * 0.01
          : 1 / (1 + Math.abs(deltaSinceLastFrame) * 0.01);
        ctx.setViewport(zoomAt(ctx.viewport, factor, this.scrubby.originX, this.scrubby.originY));
        this.scrubby.lastX = s.x;
      }
      // Clear marquee while in scrubby mode.
      this.marquee = null;
      ctx.requestRender();
    } else {
      // Update marquee rectangle.
      if (this.marquee) {
        this.marquee.endX = s.x;
        this.marquee.endY = s.y;
        ctx.requestRender();
      }
    }
  }

  onPointerUp(ctx: ToolContext, s: PointerSample): void {
    const scrubby = this.scrubby;
    this.scrubby = null;
    const marquee = this.marquee;
    this.marquee = null;

    if (!scrubby) return;

    const totalDx = s.x - scrubby.originX;
    const totalDy = s.y - scrubby.originY;
    const dist = Math.sqrt(totalDx * totalDx + totalDy * totalDy);

    // If we ended in scrubby mode (horizontal motion dominant and > 5px) the
    // zoom was already applied continuously in onPointerMove — nothing to do.
    if (Math.abs(totalDx) > Math.abs(totalDy) && Math.abs(totalDx) > 5) {
      return;
    }

    if (marquee && dist > 10) {
      // Marquee zoom: fit viewport to the defined region
      const { startX, startY, endX, endY } = marquee;
      const rect = {
        x: Math.min(startX, endX),
        y: Math.min(startY, endY),
        w: Math.abs(endX - startX),
        h: Math.abs(endY - startY),
      };
      if (rect.w > 10 && rect.h > 10) {
        // Get viewport dimensions from the injected provider (falls back to 800x600 in tests)
        const vpSize = this.viewportSizeProvider ? this.viewportSizeProvider() : { width: 800, height: 600 };
        const vpWidth = vpSize.width;
        const vpHeight = vpSize.height;
        // Calculate the zoom factor to fit the rect into the viewport
        const factorX = vpWidth / rect.w;
        const factorY = vpHeight / rect.h;
        const factor = Math.min(factorX, factorY);
        // The center of the rect in CSS pixels
        const centerX = rect.x + rect.w / 2;
        const centerY = rect.y + rect.h / 2;
        ctx.setViewport(zoomAt(ctx.viewport, factor, centerX, centerY));
      }
    } else {
      // Single click: zoom in/out at click point
      const factor = this.altHeld ? 0.5 : 2;
      ctx.setViewport(zoomAt(ctx.viewport, factor, s.x, s.y));
    }
  }

  /** Get current marquee rect for rendering (or null). */
  getMarquee(): { x: number; y: number; w: number; h: number } | null {
    if (!this.marquee) return null;
    const { startX, startY, endX, endY } = this.marquee;
    const x = Math.min(startX, endX);
    const y = Math.min(startY, endY);
    const w = Math.abs(endX - startX);
    const h = Math.abs(endY - startY);
    if (w < 5 || h < 5) return null;
    return { x, y, w, h };
  }
}
